import React, { useEffect, useState, useRef } from 'react';
import { Box, CircularProgress } from '@mui/material';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import { useSearchParams } from 'react-router-dom';

import SideBar, { SIDEBAR_WIDTH } from './Sidebar';
import CustomMap from '../CustomMap';
import MediaDisplay from '../MediaDisplay';
import Header from '../Header';
import { auth } from '../../firebase';
import { callFirebaseFunction, getSignedURLs } from '../../utils/helpers';

import './styles.css';

const Home = () => {
	const [csvDataRead, setCsvDataRead] = useState(false);
	const [directories, setDirectories] = useState([]);
	const [projectInfo, setProjectInfo] = useState();
	const [selectedOrganization, setSelectedOrganization] = useState();
	const [selectedDirectoryId, setSelectedDirectoryId] = useState();
	const [loading, setLoading] = useState(false);
	const [currentImage, setCurrentImage] = useState();
	const [currentIndex, setcurrentIndex] = useState(0);
	const [imageIndexes, setImageIndexes] = useState({
		fromIndex: 0,
		toIndex: 9,
	});
	const [user, setUser] = useState();
	const [organizations, setOrganizations] = useState([]);
	const [orgsLoading, setOrgsLoading] = useState(true);
	const [directoriesLoading, setDirectoriesLoading] = useState(false);
	const [emptyOrgIds, setEmptyOrgIds] = useState(new Set());
	const [currentCoordinate, setCurrentCoordinate] = useState();
	const [sidebarOpen, setSidebarOpen] = useState(true);

	const [searchParams, setSearchParams] = useSearchParams();
	const hydratedFromUrlRef = useRef(false);

	useEffect(() => {
		const initUser = async () => {
			try {
				console.log('🔄 Initializing user...');
				// Get user access from new Cloud Function
				const accessData = await callFirebaseFunction('getUserAccess', {});
				console.log('✅ User access data received:', accessData);
				
				setUser({
					email: auth.currentUser.email,
					name: auth.currentUser.displayName,
					privilegeLevel: accessData.privilegeLevel,
					organizationIds: accessData.organizationIds || [],
					directoryIds: accessData.directoryIds || [],
				});
			} catch (error) {
				console.error('❌ Error getting user access:', error);
				console.error('Error details:', error.response?.data || error.message);
				// Fallback to token claims
				try {
					const idTokenResult = await auth.currentUser.getIdTokenResult();
					const claims = idTokenResult.claims;
					console.log('⚠️ Using fallback token claims:', claims);
					setUser({
						email: claims.email,
						name: claims.name,
						organizationIds: claims.organizationIds || [],
						privilegeLevel: claims.privilegeLevel,
						directoryIds: [],
					});
				} catch (fallbackError) {
					console.error('❌ Fallback also failed:', fallbackError);
				}
			}
		};
		
		if (auth.currentUser) {
			console.log('👤 Current user:', auth.currentUser.email);
			initUser();
		} else {
			console.warn('⚠️ No current user found');
		}
	}, []);

	// Immediately restore org from URL on mount (no auth needed, avoids blank org during init)
	useEffect(() => {
		const orgId = searchParams.get('org');
		if (orgId) setSelectedOrganization(orgId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (selectedOrganization) {
			fetchDirectories();
		}
	}, [selectedOrganization]);

	useEffect(() => {
		if (projectInfo?.images.length > 0 && !currentImage && !hydratedFromUrlRef.current) {
			updateInitialImage();
		}
	}, [projectInfo, currentImage]);

	useEffect(() => {
		if (user) {
			fetchOrganizations();
		}
	}, [user]);

	const updateInitialImage = () => {
		setCurrentImage({
			fileName: projectInfo.images[0].fileName,
		});
	};

	const fetchOrganizations = async () => {
		console.log('🔄 Fetching organizations...');
		console.log('User state:', user);
		
		try {
			// Use new Cloud Function instead of direct Firestore read
			const organizations = await callFirebaseFunction('getOrganizations', {});
			console.log('✅ Organizations received:', organizations);
			console.log('Organizations count:', organizations.length);
			console.log('Organizations array:', JSON.stringify(organizations, null, 2));
			
			setOrganizations(organizations);
			setOrgsLoading(false);
			
			const orgFromUrl = searchParams.get('org');
			const defaultOrg = organizations[0] && organizations[0].id;
			const chosenOrg = organizations.find((o) => o.id === orgFromUrl)?.id || defaultOrg;
			
			console.log('Org from URL:', orgFromUrl);
			console.log('Default org:', defaultOrg);
			console.log('Chosen org:', chosenOrg);
			
			setSelectedOrganization(chosenOrg);
			
			console.log('Selected organization:', chosenOrg);
			
			// Initialize URL org if missing
			if (!orgFromUrl && chosenOrg) {
				setSearchParams(
					(prev) => {
						const next = new URLSearchParams(prev);
						next.set('org', chosenOrg);
						next.delete('dir');
						next.delete('proj');
						next.delete('idx');
						return next;
					},
					{ replace: true }
				);
			}
		} catch (error) {
			console.error('❌ Error fetching organizations:', error);
			console.error('Error details:', error.response?.data || error.message);
			setOrgsLoading(false);
			setOrganizations([]);
		}
	};

	const fetchDirectories = async () => {
		console.log('🔄 Fetching directories for org:', selectedOrganization);
		setDirectoriesLoading(true);
		try {
			// Use new Cloud Function instead of direct Firestore read
			const directories = await callFirebaseFunction('getDirectories', {
				organizationId: selectedOrganization
			});
			
			console.log('✅ Directories received:', directories);
			setDirectories(directories);
			if (directories.length === 0 && user?.privilegeLevel === 0) {
				setEmptyOrgIds((prev) => new Set([...prev, selectedOrganization]));
			}
		} catch (error) {
			console.error('❌ Error fetching directories:', error);
			console.error('Error details:', error.response?.data || error.message);
			setDirectories([]);
		} finally {
			setDirectoriesLoading(false);
		}
	};

	const loadProject = async (project, directoryId) => {
		// Reset CSV state FIRST before changing project
		setCsvDataRead(false);
		
		setImageIndexes({
			fromIndex: 0,
			toIndex: 9,
		});
		setcurrentIndex(0);
		setSidebarOpen(false);
		
		if (project?.images && project.images.length > 0) {
			setCurrentImage({
				fileName: project.images[0].fileName,
			});
			
			// Load initial batch of signed URLs for images
			const initialBatch = project.images.slice(0, 10).map(img => 
				`${project.id}/images/${img.fileName}`
			);
			
			try {
				await getSignedURLs(project.id, initialBatch);
				// URLs are cached automatically
			} catch (error) {
				console.error('Error loading image URLs:', error);
			}
			
			// Generate signed URL for CSV file
			if (project.csvURL) {
				try {
					const csvKey = `${project.id}/reference.csv`;
					const csvUrls = await getSignedURLs(project.id, [csvKey]);
					const signedCsvUrl = csvUrls[csvKey];
					
					if (signedCsvUrl) {
						// Update project with signed CSV URL
						project = {
							...project,
							csvURL: signedCsvUrl
						};
					}
				} catch (error) {
					console.error('Error loading CSV URL:', error);
				}
			}
		} else {
			setCurrentImage(undefined);
		}
		
		setSelectedDirectoryId(directoryId);
		setProjectInfo(project);

		// Update URL
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				if (selectedOrganization) next.set('org', selectedOrganization);
				if (directoryId) next.set('dir', directoryId);
				next.set('proj', project.id);
				next.set('idx', '0');
				return next;
			},
			{ replace: true }
		);
	};

	const nextImage = () => {
		setLoading(true);
		setcurrentIndex((currentIndex) => {
			let newIndex = currentIndex + 1;
			if (newIndex >= projectInfo.images.length) {
				newIndex = 0;
			}
			
			// Prefetch next batch of images if approaching end
			if (newIndex > imageIndexes.toIndex - 4) {
				const nextBatch = projectInfo.images
					.slice(imageIndexes.toIndex, imageIndexes.toIndex + 10)
					.map(img => `${projectInfo.id}/images/${img.fileName}`);
				
				if (nextBatch.length > 0) {
					getSignedURLs(projectInfo.id, nextBatch).catch(console.error);
				}
				
				setImageIndexes((prevState) => ({
					fromIndex: Math.max(currentIndex - 4, 0),
					toIndex: Math.min(prevState.toIndex + 4, projectInfo.images.length),
				}));
			}
			
			setCurrentImage({
				fileName: projectInfo.images[newIndex].fileName,
			});
			setLoading(false);
			
			// Sync URL index
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					if (selectedOrganization) next.set('org', selectedOrganization);
					if (selectedDirectoryId) next.set('dir', selectedDirectoryId);
					if (projectInfo?.id) next.set('proj', projectInfo.id);
					next.set('idx', String(newIndex));
					return next;
				},
				{ replace: true }
			);
			return newIndex;
		});
	};

	const prevImage = () => {
		setLoading(true);
		if (currentIndex !== 0) {
			setcurrentIndex((currentIndex) => {
				let newIndex = currentIndex - 1;
				if (newIndex < 0) {
					newIndex = projectInfo.images.length - 1;
				}
				
				// Prefetch previous batch if needed
				if (newIndex < imageIndexes.fromIndex) {
					const prevBatch = projectInfo.images
						.slice(Math.max(0, imageIndexes.fromIndex - 10), imageIndexes.fromIndex)
						.map(img => `${projectInfo.id}/images/${img.fileName}`);
					
					if (prevBatch.length > 0) {
						getSignedURLs(projectInfo.id, prevBatch).catch(console.error);
					}
					
					setImageIndexes((prevState) => ({
						fromIndex: Math.max(prevState.fromIndex - 4, 0),
						toIndex: newIndex + 4,
					}));
				}
				
				setCurrentImage({
					fileName: projectInfo.images[newIndex].fileName,
				});
				setLoading(false);
				// Sync URL index
				setSearchParams(
					(prev) => {
						const next = new URLSearchParams(prev);
						if (selectedOrganization) next.set('org', selectedOrganization);
						if (selectedDirectoryId) next.set('dir', selectedDirectoryId);
						if (projectInfo?.id) next.set('proj', projectInfo.id);
						next.set('idx', String(newIndex));
						return next;
					},
					{ replace: true }
				);
				return newIndex;
			});
		}
	};

	const switchOrganization = (organizationId) => {
		setSelectedOrganization(organizationId);
		setSelectedDirectoryId(undefined);
		setProjectInfo(undefined);
		setCurrentImage(undefined);
		setcurrentIndex(0);
		setImageIndexes({ fromIndex: 0, toIndex: 9 });
		setSearchParams({ org: organizationId }, { replace: true });
	};

	const onClickMarker = (index) => {
		const newIndex = index >= projectInfo.images.length ? 0 : index;
		setcurrentIndex(newIndex);
		setImageIndexes({
			fromIndex: Math.max(index - 4, 0),
			toIndex: Math.min(index + 4, projectInfo.images.length),
		});
		setCurrentImage({
			fileName: projectInfo.images[index].fileName,
			token: projectInfo.images[index].token,
		});
		// Sync URL index
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				if (selectedOrganization) next.set('org', selectedOrganization);
				if (selectedDirectoryId) next.set('dir', selectedDirectoryId);
				if (projectInfo?.id) next.set('proj', projectInfo.id);
				next.set('idx', String(newIndex));
				return next;
			},
			{ replace: true }
		);
	};

	// Hydrate from URL once auth (user) is ready — avoids the auth-not-ready race on page refresh
	useEffect(() => {
		if (!user || hydratedFromUrlRef.current) return;

		const orgId = searchParams.get('org');
		const dirId = searchParams.get('dir');
		const projId = searchParams.get('proj');
		const idxParam = searchParams.get('idx');

		if (!orgId || !dirId || !projId) return;

		console.log('🔄 Hydrating from URL:', { orgId, dirId, projId });

		callFirebaseFunction('getProjects', {
			directoryId: dirId,
			organizationId: orgId,
		}).then(async (projects) => {
			const project = projects.find(p => p.id === projId);

			if (project) {
				console.log('✅ Project loaded from URL:', project.name);
				setSelectedDirectoryId(dirId);

				// Generate signed URL for CSV file
				if (project.csvURL) {
					try {
						const csvKey = `${project.id}/reference.csv`;
						const csvUrls = await getSignedURLs(project.id, [csvKey]);
						const signedCsvUrl = csvUrls[csvKey];

						if (signedCsvUrl) {
							project.csvURL = signedCsvUrl;
						}
					} catch (error) {
						console.error('Error loading CSV URL:', error);
					}
				}

				setProjectInfo(project);
				setSidebarOpen(false);

				const parsedIdx = Number.parseInt(idxParam || '0', 10);
				const safeIdx = Number.isFinite(parsedIdx)
					? Math.max(0, Math.min(parsedIdx, (project.images?.length || 1) - 1))
					: 0;

				setcurrentIndex(safeIdx);

				if (project.images && project.images[safeIdx]) {
					setCurrentImage({
						fileName: project.images[safeIdx].fileName,
					});
				}

				setImageIndexes({
					fromIndex: Math.max(safeIdx - 4, 0),
					toIndex: Math.min(safeIdx + 4, project.images?.length || 0),
				});

				hydratedFromUrlRef.current = true;
			} else {
				console.warn('⚠️ Project not found:', projId);
			}
		}).catch(error => {
			console.error('❌ Error loading project from URL:', error);
		});
	// searchParams is stable from useSearchParams on mount; user is the trigger
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user]);

	// If the selected org becomes empty (for a USER), auto-switch to the first visible org
	useEffect(() => {
		if (user?.privilegeLevel !== 0) return;
		if (!selectedOrganization || !emptyOrgIds.has(selectedOrganization)) return;
		const nextOrg = organizations.find((o) => !emptyOrgIds.has(o.id));
		if (nextOrg) {
			switchOrganization(nextOrg.id);
		}
	}, [emptyOrgIds, selectedOrganization, organizations, user]);

	// Auto-open sidebar when no project is selected
	useEffect(() => {
		if (!projectInfo) {
			setSidebarOpen(true);
		}
	}, [projectInfo]);

	// Clear project state when the URL loses the proj param (e.g. clicking the Inspections heading)
	useEffect(() => {
		if (!searchParams.get('proj') && projectInfo) {
			setProjectInfo(undefined);
			setSelectedDirectoryId(undefined);
			setCurrentImage(undefined);
			setcurrentIndex(0);
			setImageIndexes({ fromIndex: 0, toIndex: 9 });
			hydratedFromUrlRef.current = false;
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams]);

	const directoryName = directories.find((d) => d.id === selectedDirectoryId)?.name;

	return organizations ? (
		<div style={{ height: '100vh', overflow: 'hidden', backgroundColor: projectInfo ? '#0f2d4e' : undefined }}>
			<SideBar
				loadProject={loadProject}
				directories={directories}
				organizationId={selectedOrganization}
				user={user}
			organizations={user?.privilegeLevel === 0 ? organizations.filter((o) => !emptyOrgIds.has(o.id)) : organizations}
				orgsLoading={orgsLoading}
				directoriesLoading={directoriesLoading}
				switchOrganization={switchOrganization}
				selectedProjectId={projectInfo?.id}
				open={sidebarOpen}
				onOpenChange={setSidebarOpen}
			/>
			<section
				className="home__page-content"
				style={{
					marginLeft: sidebarOpen ? SIDEBAR_WIDTH : 44,
					transition: 'margin-left 0.25s ease',
					...(projectInfo ? { padding: 0, position: 'relative' } : {}),
				}}
			>
				<Header overlay={!!projectInfo} />
				{projectInfo ? (
					<div className="workspace">
						<div className="workspace__chrome">
						<div className="workspace__project-info">
							{directoryName && (
								<span className="workspace__dir-label">{directoryName}</span>
							)}
							{directoryName && (
								<span className="workspace__breadcrumb-sep">/</span>
							)}
							<span className="workspace__project-name">{projectInfo.name}</span>
							{projectInfo.images?.length > 0 && (
								<span className="workspace__image-count">
									{currentIndex + 1}&thinsp;/&thinsp;{projectInfo.images.length}
								</span>
							)}
						</div>
						</div>
						<div className="workspace__panels">
							<div className="workspace__map-panel">
								<CustomMap
									csvURL={projectInfo.csvURL}
									csvDataRead={csvDataRead}
									updateCSVDataReadingState={(read) => setCsvDataRead(read)}
									currentImageFileName={currentImage?.fileName}
									onClickMarker={onClickMarker}
									currentImageIndex={currentIndex}
									onCoordinateChange={(coord) => setCurrentCoordinate(coord)}
								/>
							</div>
							<div className="workspace__viewer-panel">
								<MediaDisplay
									nextImage={nextImage}
									prevImage={prevImage}
									images={projectInfo.images || []}
									imageIndexes={imageIndexes}
									loading={loading}
									currentImage={currentImage}
									orgId={selectedOrganization}
									projectId={projectInfo.id}
									projectName={projectInfo.name}
									currentCoordinate={currentCoordinate}
									currentIndex={currentIndex}
								/>
							</div>
						</div>
					</div>
				) : (
					<div className="home__empty-state">
						<div className="home__empty-panels">
							<div className="home__empty-panel">
								<MapOutlinedIcon sx={{ fontSize: 36, color: 'rgba(15, 45, 78, 0.34)' }} />
								<span className="home__empty-panel-text">Route map will appear here</span>
							</div>
							<div className="home__empty-panel">
								<PhotoLibraryOutlinedIcon sx={{ fontSize: 36, color: 'rgba(15, 45, 78, 0.34)' }} />
								<span className="home__empty-panel-text">Inspection imagery will appear here</span>
							</div>
						</div>
						<div className="home__empty-message">
							<h2 className="home__empty-heading">No project selected</h2>
							<p className="home__empty-body">
								Choose a project from the left sidebar to view route paths, captured imagery, and inspection details.
							</p>
							<p className="home__empty-hint">
								Expand a folder in the sidebar to browse projects.
							</p>
						</div>
					</div>
				)}
			</section>
		</div>
	) : (
		<Box
			sx={{
				position: 'absolute',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				backgroundColor: 'rgba(255, 255, 255, 0.8)',
				zIndex: 2,
			}}
		>
			<CircularProgress />
		</Box>
	);
};

export default Home;
